import React from "react";
import {CustomTextField} from "../../../../../components/CustomTextField";
import ButtonIcon from "../../../../../components/buttons/ButtonIcon";
import {CleanIcon, IconAreaTable, IconMapPinPlus, IconNotebook} from "../../../../../components/buttons/IconButtons";
import CustomAvailableItems from "../../../../../components/CustomAvailableAssignedItems/CustomAvailableItems";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {TABLES} from "../../../../../Apis/Tables";
import ZoneItemCard from "../ZoneItemCard";
import ZoneAccessItemTabs from "../ZoneAccessItemTabs";
import ZoneStepApp from "../../Functionality/ZoneStepApp";

/**
 * Keeps the zones whose name contains the searched text.
 */
const filterZonesByName = (zones, searchElement) =>
    zones.filter((zone) => zone.name?.toLowerCase().includes(searchElement.toLowerCase()));

/**
 * Name of the zone access and its zones, picked from the available ones paged and searched by name.
 * The clear button is only shown on creation. On the update, the zones are split in a tab to add
 * new ones and a tab with the ones already assigned.
 *
 * @param {string} zoneAccessId Zone access being updated, whose zones are listed as assigned.
 * @param {boolean} isUpdate Whether the zone access is being updated.
 * @param {string} name Name of the zone access.
 * @param {Function} setName Setter of the name.
 * @param {Array} selectedZones Zones picked, kept by the form to validate and save them.
 * @param {Function} setSelectedZones Setter of the picked zones.
 * @param {Array} assignedZones Zones already assigned to the zone access being updated.
 * @param {Function} removeAssignedZones Removes the given zone ids from the zone access right away.
 * @param {boolean} canSave Whether the assigned zones can be removed.
 * @param {boolean} disabled Read-only, the zone access is only shown.
 * @param {boolean} emptyFields Flags the name and the selected section when they are empty.
 * @param {Function} handleClearFields Clears the name and the picked zones.
 */
const ZoneStep = ({
                      zoneAccessId,
                      isUpdate,
                      name,
                      setName,
                      selectedZones,
                      setSelectedZones,
                      assignedZones,
                      removeAssignedZones,
                      canSave,
                      disabled = false,
                      emptyFields,
                      handleClearFields
                  }) => {
    const {
        zoneResults,
        setZoneResults,
        zoneSearchElement,
        setZoneSearchElement,
        setZoneSearch,
        setZoneSimpleSearch,
        setZoneBodyForExport,
        zoneModified,
        setZoneModified,
        zoneTableBuildBody,
    } = ZoneStepApp();

    const noZones = selectedZones.length === 0 && assignedZones.length === 0;

    const availableItems = (
        <CustomAvailableItems
            id={null}
            results={zoneResults}
            setResults={setZoneResults}
            searchElement={zoneSearchElement}
            setSearchElement={setZoneSearchElement}
            setSearch={setZoneSearch}
            setSimpleSearch={setZoneSimpleSearch}
            setBodyForExport={setZoneBodyForExport}
            modified={zoneModified}
            setModified={setZoneModified}
            selected={selectedZones}
            setSelected={setSelectedZones}
            keyId={'id'}
            tableEndPoint={SimpleSearch}
            tableBuildBody={zoneTableBuildBody}
            labelRowsPerPage={'zone_per_page'}
            titleContainer={'available_zones'}
            selectedSectionTitle={'selected_zones'}
            searchTitle={'search_by_name'}
            tableNotInformationLegend={'you_have_not_created_any_zones_yet'}
            selectedSectionNotInformationLegend={'nothing_selected_yet'}
            tableIconTitle={<IconNotebook className={"icon-color-general-950"}/>}
            selectedSectionIconTitle={<IconAreaTable width={24} height={24} className={"icon-color-general-950"}/>}
            renderAvailableItem={({item, disabled, onAdd}) => (
                <ZoneItemCard item={item} disabled={disabled} onAdd={onAdd}/>
            )}
            renderSelectedItems={({items, selectedSearchElement, handleDelete}) =>
                filterZonesByName(items, selectedSearchElement).map((zone) => (
                    <div key={zone.id} className={"mb-3"}>
                        <ZoneItemCard item={zone} onDelete={handleDelete}/>
                    </div>
                ))
            }
            emptyFields={emptyFields && noZones}
            // Below the name field the list starts too low to size it against the viewport, as the assigned tab
            autoHeight={false}
        />
    );

    return (
        <>
            {!isUpdate &&
                <div className={"d-flex justify-content-end mt-4 mb-3"}>
                    <ButtonIcon
                        id={"btn_zaf_clean_fields"}
                        Icon={CleanIcon}
                        onClick={handleClearFields}
                        title={'clear_instances'}
                        className={"btn-clean-secondary-600"}
                        tooltipPlacement={"right"}
                        iconProps={"btn-icon-svg-12px icon-color-general-00"}
                    />
                </div>
            }
            <div className={`row`}>
                <CustomTextField
                    id={"txt_zaf_name"}
                    disabled={disabled}
                    name={"name"}
                    value={name}
                    trans={"name"}
                    type={"text"}
                    length={128}
                    onChange={(e) => setName(e.target.value)}
                    emptyFields={emptyFields}
                    required={true}
                />
            </div>
            {isUpdate
                ?
                <ZoneAccessItemTabs
                    id={'zaf_zones'}
                    zoneAccessId={zoneAccessId}
                    addTab={{title: 'add_zones', icon: <IconMapPinPlus width={24} height={24}/>}}
                    assignedTab={{title: 'assigned_zones', icon: <IconAreaTable width={24} height={24}/>}}
                    addContent={availableItems}
                    assigned={assignedZones}
                    assignedList={{
                        model: TABLES.ZONE_ACCESS_ASSIGNED_ZONE,
                        keyId: 'id',
                        labelRowsPerPage: 'zone_per_page',
                        searchTitle: 'search_by_name',
                        notInformationLegend: 'nothing_selected_yet',
                        renderAssignedItem: ({item, onDelete, checkbox, handleCheckboxChange, selected}) => (
                            <ZoneItemCard
                                item={item}
                                checked={selected.includes(item.id)}
                                onCheck={checkbox
                                    ? (zone, checked) => handleCheckboxChange({target: {id: zone.id, checked}})
                                    : undefined
                                }
                                onDelete={onDelete}
                            />
                        ),
                    }}
                    canDelete={canSave}
                    disabled={disabled}
                    onRemove={removeAssignedZones}
                    removeTitle={'remove_zones'}
                    removeElement={'zones'}
                    results={zoneResults}
                    setResults={setZoneResults}
                    searchElement={zoneSearchElement}
                    setSearchElement={setZoneSearchElement}
                    setSearch={setZoneSearch}
                    setSimpleSearch={setZoneSimpleSearch}
                    setBodyForExport={setZoneBodyForExport}
                    modified={zoneModified}
                    setModified={setZoneModified}
                />
                :
                availableItems
            }
        </>
    );
};

export default ZoneStep;
